<script setup lang="ts">
import { computed, onMounted } from 'vue'
import type { Study } from '@pocketprep/types'
import { examModule } from '@/store/exam/module'
import { userModule } from '@/store/user/module'
import { compareSemanticVersionsDescending, formatDate } from '@/utils'
import ContentVersionBanner from '@/components/ContentVersionBanner.vue'
import { analyticsModule } from '@/store/analytics/module'
import { bundleModule } from '@/store/bundle/module'

type TMessage = {
    name: Study.Class.MessageName
    examMetadataId: string
    version: string
    timestamp: number
}

const currentUser = computed(() => {
    return userModule.state.user
})

const currentExamMetadata = computed(() => {
    return examModule.getters.getCurrentExamMetadata()
})

const currentExamVersions = computed(() => {
    return examModule.getters.getCurrentExamVersions()
})

const currentOrgExam = computed(() => {
    return examModule.getters.getCurrentGroupExam()
})

const webConfigMessagesSeen = computed(() => {
    const messagesSeen = [ ...currentUser.value?.webConfig?.messagesSeen || [] ]
    return messagesSeen?.sort((a, b) => a.timestamp - b.timestamp)
})

const messageTitle = computed(() => {
    const oldExamDiscontinueDate = sunsettedExam.value
        && examModule.getters.getDiscontinuedDateForExam(sunsettedExam.value)
    const minorVersionMessaging = currentExamMetadata.value?.minorVersionMessaging
    if (currentMessageName.value === 'teachMajorNewVersion') {
        return `Multiple versions of ${currentExamMetadata.value?.nativeAppName} are now available.`
    } else if (currentMessageName.value === 'teachSunsetWillDiscontinue') {
        return `${currentExamMetadata.value?.nativeAppName} launched a new version of their exam.\
        Our prep for the previous version will be discontinued on ${
    oldExamDiscontinueDate ? formatDate(oldExamDiscontinueDate) : 'a future date'}.`
    } else if (currentMessageName.value === 'teachSunsetWasDiscontinued') {
        return `${sunsettedExam.value?.releaseInfo.name} has been discontinued`
    } else if (currentMessageName.value === 'teachMinorNewMockExams') {
        return `${minorVersionMessaging?.count} New Mock Exam${minorVersionMessaging
            && minorVersionMessaging.count > 1
            ? 's were'
            : ' was'} added to ${
            currentExamMetadata.value?.nativeAppName} exam prep.`
    } else if (currentMessageName.value === 'teachMinorNewQuestions') {
        return `${minorVersionMessaging?.count} question${
            minorVersionMessaging?.count === 1 ? '' : 's'} were added to our ${
            currentExamMetadata.value?.nativeAppName} question bank.`
    } else {
        return ''
    }
})

const showMultipleMajorVersionsMessage = computed(() => {
    const isMultipleMajorVersionsAvailable = currentExamVersions.value?.filter(examVersion =>
        !!bundleModule.getters.getExamBundle(examVersion.objectId) && !examVersion.sunsetAt
    ).length > 1
    if (!isMultipleMajorVersionsAvailable) {
        return false
    }
    return !isMessageDismissed('teachMajorNewVersion')
})

const showSunsetMessage = computed(() => {
    const sunsetDateIso = sunsettedExam.value?.sunsetAt?.iso
    if (!sunsetDateIso) {
        return false
    }
    const isBeforeSunset = isBeforeOrAfterSunset(sunsetDateIso) === 'before' ? true : false
    if (!isBeforeSunset) {
        return false
    }
    return !isMessageDismissed('teachSunsetWillDiscontinue')
})

const showDiscontinuedMessage = computed(() => {
    const sunsetDateIso = sunsettedExam.value?.sunsetAt?.iso
    if (!sunsetDateIso) {
        return false
    }
    const discontinueDate = examModule.getters.getDiscontinuedDateForExam(sunsettedExam.value)
    const orgExamCreatedAt = currentOrgExam.value?.createdAt ? new Date(currentOrgExam.value.createdAt) : undefined
    if (!orgExamCreatedAt || !discontinueDate) {
        return false
    }
    const wasExamAddedAfterDiscontinueDate = orgExamCreatedAt.getTime() > discontinueDate.getTime()
    if (wasExamAddedAfterDiscontinueDate) {
        return false
    }
    const isAfterSunset = isBeforeOrAfterSunset(sunsetDateIso) === 'after' ? true : false
    if (!isAfterSunset) {
        return false
    }
    return !isMessageDismissed('teachSunsetWasDiscontinued')
})

const showNewMockExamMinorVersionMessage = computed(() => {
    const minorVersionMessagingType = currentExamMetadata.value?.minorVersionMessaging?.type
    const minorVersionTeachType = minorVersionMessagingType && 'teachMinor' + minorVersionMessagingType
        .charAt(0).toUpperCase() + minorVersionMessagingType.slice(1) as Extract<TMessage['name'], `teach${string}`>
    if (minorVersionTeachType !== 'teachMinorNewMockExams') {
        return false
    }
    return !isMessageDismissed(minorVersionTeachType)
})

const showNewQuestionsMinorVersionMessage = computed(() => {
    const minorVersionMessagingType = currentExamMetadata.value?.minorVersionMessaging?.type
    const minorVersionTeachType = minorVersionMessagingType && 'teachMinor' + minorVersionMessagingType
        .charAt(0).toUpperCase() + minorVersionMessagingType.slice(1) as Extract<TMessage['name'], `teach${string}`>
    if (minorVersionTeachType !== 'teachMinorNewQuestions') {
        return false
    }
    return !isMessageDismissed(minorVersionTeachType)
})

const latestMajor = computed(() => {
    const currentVersions = currentExamVersions.value
        .filter(examVersion => !!bundleModule.getters.getExamBundle(examVersion.objectId))
    return currentVersions.sort((a, b) =>
        compareSemanticVersionsDescending(a.version, b.version))[0]
})

const sunsettedExam = computed(() => {
    return currentExamVersions.value.find(examVersion => examVersion.sunsetAt)
})

const currentMessageName = computed<Extract<TMessage['name'], `teach${string}`> | null>(() => {
    if (showDiscontinuedMessage.value) {
        return 'teachSunsetWasDiscontinued'
    } else if (showSunsetMessage.value) {
        return 'teachSunsetWillDiscontinue'
    } else if (showMultipleMajorVersionsMessage.value) {
        return 'teachMajorNewVersion'
    } else if (showNewMockExamMinorVersionMessage.value) {
        return 'teachMinorNewMockExams'
    } else if (showNewQuestionsMinorVersionMessage.value) {
        return 'teachMinorNewQuestions'
    } else {
        return null
    }
})

onMounted(async () => {
    await bundleModule.actions.fetchBundles()
    if (currentMessageName.value) {
        await submitVersionMessageSeen(currentMessageName.value)
    }
})

const fireAmplitudeEventMessageSeen = () => {
    const messageName = currentMessageName.value
    if (getAnExistingMessageMatch(messageName)) {
        return
    }
    if (messageName === 'teachSunsetWasDiscontinued' || messageName === 'teachSunsetWillDiscontinue') {
        const discontinueDate = sunsettedExam.value
            && examModule.getters.getDiscontinuedDateForExam(sunsettedExam.value)
        const discontinueDateString = discontinueDate?.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }) || ''
        const eventProperties = {
            sunsetMessagingType: messageName === 'teachSunsetWasDiscontinued' ? 'wasDiscontinued' : 'willDiscontinue',
            examName: sunsettedExam.value?.nativeAppName,
            oldExamVersionName: sunsettedExam.value?.releaseInfo.name,
            sunsetDate: sunsettedExam.value?.sunsetAt?.iso,
            discontinueDateString,
            examGuid: sunsettedExam.value?.examGuid,
            bundleId: sunsettedExam.value
                && bundleModule.getters.getExamBundle(sunsettedExam.value?.objectId)?.objectId || '',
        }
        if (Object.values(eventProperties).every(value => !!value)) {
            analyticsModule.actions.amplitudeTrack('Instructor Sunset Message Triggered', eventProperties)
        }
    } else if (messageName === 'teachMajorNewVersion') {
        const eventProperties = {
            examName: currentExamMetadata.value?.nativeAppName,
            oldExamVersionName: sunsettedExam.value?.releaseInfo.name,
            examGuid: currentExamMetadata.value?.examGuid,
            bundleId: currentExamMetadata.value
                && bundleModule.getters.getExamBundle(currentExamMetadata.value.objectId)?.objectId || '',
        }
        if (Object.values(eventProperties).every(value => !!value)) {
            analyticsModule.actions.amplitudeTrack('Instructor Major Update Message Triggered', eventProperties)
        }
    }
}

const extractMajorNumber = (version: string) => {
    const majorNumber = version.split('.')
    return majorNumber[0]
}

const extractMinorNumber = (version: string) => {
    const majorNumber = version.split('.')
    return `${majorNumber[0]}.${majorNumber[1]}`
}

const isTimestampSevenDaysOld = (timestamp: number) => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
    return timestamp <= sevenDaysAgo
}

const isBeforeOrAfterSunset = (sunsetAtIso: string): 'before' | 'after' | undefined => {
    const sunsetAtIsoDateMinusZ = sunsetAtIso.slice(0, -1)
    const sunsetAtDate = new Date(sunsetAtIsoDateMinusZ)
    const currentDate = new Date()

    // Calculate one month before and after the sunset date
    const oneMonthBefore = new Date(sunsetAtDate)
    oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1)

    const oneMonthAfter = new Date(sunsetAtDate)
    oneMonthAfter.setMonth(oneMonthAfter.getMonth() + 1)

    // Check if we are inside of a sunset range
    if (currentDate >= oneMonthBefore && currentDate <= oneMonthAfter) {
        return 'before'
    } else if (currentDate > oneMonthAfter) {
        return 'after'
    }
}

const isMessageDismissed = (messageName: Extract<TMessage['name'], `teach${string}`>) => {
    const existingMessage = getAnExistingMessageMatch(messageName)
    if (!existingMessage || (existingMessage && !isTimestampSevenDaysOld(existingMessage.timestamp))) {
        return false
    } else {
        return true
    }
}

const getAnExistingMessageMatch = (messageName: Extract<TMessage['name'], `teach${string}`> | null) => {
    if (!messageName) {
        return
    }
    const { version, examMetadataId } = getExamVersionAndMetadataIdByMessage(messageName)
    const currentMessageState: TMessage = {
        name: messageName,
        examMetadataId: examMetadataId || '',
        version: version || '',
        timestamp: Date.now(),
    }
    const existingMessages = webConfigMessagesSeen.value
    const messageMatch = existingMessages?.find(existingMessage =>
        existingMessage.examMetadataId === currentMessageState.examMetadataId
        && existingMessage.name === currentMessageState.name
        && existingMessage.version === currentMessageState.version
    )
    return messageMatch
}

const getExamVersionAndMetadataIdByMessage = (messageName: Extract<TMessage['name'], `teach${string}`>) => {
    let examMetadataId
    let version
    if (messageName === 'teachMajorNewVersion') {
        examMetadataId = latestMajor.value?.objectId
        version = latestMajor.value && extractMajorNumber(latestMajor.value?.version)
    } else if (messageName === 'teachSunsetWillDiscontinue') {
        examMetadataId = sunsettedExam.value?.objectId || ''
        version = sunsettedExam.value?.version || ''
    } else if (messageName === 'teachSunsetWasDiscontinued') {
        examMetadataId = sunsettedExam.value?.objectId || ''
        version = sunsettedExam.value?.version || ''
    } else {
        examMetadataId = currentExamMetadata.value?.objectId || ''
        version = currentExamMetadata.value && extractMinorNumber(currentExamMetadata.value?.version)
    }

    return {
        examMetadataId,
        version,
    }
}

const submitVersionMessageSeen = async (messageName: Extract<TMessage['name'], `teach${string}`>,
    isDismissed?: boolean) => {
    const { version, examMetadataId } = getExamVersionAndMetadataIdByMessage(messageName)
    const newMessage: TMessage = {
        name: messageName,
        examMetadataId: examMetadataId || '',
        version: version || '',
        timestamp: Date.now(),
    }
    const existingMessage = getAnExistingMessageMatch(messageName)
    if (existingMessage && !isDismissed) {
        return
    }
    const newMessagesSeen: Study.Class.UserWebConfig['messagesSeen'] = []
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
    const oldMessagesSeen = webConfigMessagesSeen.value || []
    if (isDismissed) {
        newMessage.timestamp = sevenDaysAgo
        if (existingMessage) {
            const indexOfExistingMessage = oldMessagesSeen.indexOf(existingMessage)
            oldMessagesSeen.splice(indexOfExistingMessage, 1, newMessage)
        }
    }
    newMessagesSeen.push(newMessage)
    // if minor and major appear at the same time, show only major and mark minor as seen
    if (messageName === 'teachMajorNewVersion'
        && (showNewMockExamMinorVersionMessage.value || showNewQuestionsMinorVersionMessage.value)) {
        const additionalMinorMessageName = showNewMockExamMinorVersionMessage.value
            ? 'teachMinorNewMockExams'
            : 'teachMinorNewQuestions'
        const additionalMinorMessage: TMessage = {
            name: additionalMinorMessageName,
            examMetadataId: currentExamMetadata.value?.objectId || '',
            version: currentExamMetadata.value && extractMinorNumber(currentExamMetadata.value?.version) || '',
            timestamp: isDismissed ? sevenDaysAgo : Date.now(),
        }
        newMessagesSeen.push(additionalMinorMessage)
    }

    fireAmplitudeEventMessageSeen()

    await userModule.actions.updateWebConfig({
        messagesSeen: [ ...oldMessagesSeen, ...newMessagesSeen ],
    })
}
</script>

<template>
    <ContentVersionBanner
        v-if="currentMessageName === 'teachSunsetWasDiscontinued'"
        :banner-title="messageTitle"
        @close="submitVersionMessageSeen('teachSunsetWasDiscontinued', true)"
    >
        All of your students are now studying the current version.
    </ContentVersionBanner>
    <ContentVersionBanner
        v-if="currentMessageName === 'teachSunsetWillDiscontinue'"
        :banner-title="messageTitle"
        :show-link="true"
        @close="submitVersionMessageSeen('teachSunsetWillDiscontinue', true)"
    >
        Recommend your students switch to the current version if they haven't already.
    </ContentVersionBanner>
    <ContentVersionBanner
        v-if="currentMessageName === 'teachMajorNewVersion'"
        :banner-title="messageTitle"
        :show-link="true"
        @close="submitVersionMessageSeen('teachMajorNewVersion', true)"
    >
        Your students have access to both versions. Switch between versions above to see progress for each.
    </ContentVersionBanner>
    <ContentVersionBanner
        v-if="currentMessageName === 'teachMinorNewMockExams'"
        :banner-title="messageTitle"
        @close="submitVersionMessageSeen('teachMinorNewMockExams', true)"
    >
        Your students will have even more test prep opportunities with this update. 💪
    </ContentVersionBanner>
    <ContentVersionBanner
        v-if="currentMessageName === 'teachMinorNewQuestions'"
        :banner-title="messageTitle"
        @close="submitVersionMessageSeen('teachMinorNewQuestions', true)"
    >
        Your students' question bank progress will have changed with this update.
    </ContentVersionBanner>
</template>

<style lang="scss" scoped>
// no-op
</style>
